import React, { Component } from 'react';

import { getMedia } from '../utils/productMedia';
import { buildPath } from '../utils/route_utils';
import { observeRise } from '../utils/reveal';
import { trackEvent } from '../utils/analytics';

import '../css/ProductLayout.css';

/*
 * Product page layout B: PRODUCT STAGE + TILE MOSAIC.
 *
 * Rendered by TechPage between Navbar/Breadcrumb and Footer when the URL
 * TechPage invokes this module's default export as a plain
 * function from a class render, so the export just returns a <LayoutB />
 * element; LayoutB is the real (class) component and owns the state.
 *
 * Every section spans the viewport (no boxed column, no frames, no image
 * margins): copy sits inside padding-inline: var(--bleed), media runs edge
 * to edge, grids are tiles with 1 px gaps. Media never references YAML
 * paths directly; getMedia() classifies each file and the layout stages it:
 *   cutout -> dark stage (spotlight + floor shadow), contain
 *   photo  -> cover, object-position = focal
 *   ui     -> cover on black, no padding
 *   gif    -> <video> loop when re-encoded, else the gif, cover
 *   white  -> not re-cut yet: multiplied onto a light pool so the white
 *             background becomes the spotlight (never a white plate)
 * Registry entries also carry `stage` (16:10 render on Deep Gray with its
 * own spotlight + floor shadow) and `stageM` (960x1200 portrait): the hero
 * covers its media half with them and adds a CSS stage on top. Unregistered
 * files get `kind` guessed by extension, refined by a corner-pixel sniff.
 */

const LANGS = ['zh', 'en', 'ja'];
const CONTACT_ROUTE = '/contact';
const FEATURE_TYPES = ['pic_word_section', 'word_pic_section'];
const BASE = process.env.PUBLIC_URL || '';
// "<< text >>" (or CJK brackets) at the start of an intro, plus trailing <br>s.
const STATUS_RE = /^\s*(?:<<|《|＜＜)\s*([^<>《》＜＞]+?)\s*(?:>>|》|＞＞)\s*(?:<\s*\/?\s*br\s*\/?\s*>\s*)*/i;
// Layout-side focal nudges on top of the registry, keyed by the ORIGINAL yaml path.
const FOCAL_TWEAKS = {
  '/assets/product/nxa/vs_ilegal.gif': '50% 60%'   // keeps the overlay label in frame
};
const YT_RE = /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?(?:.*&)?v=|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;
// Leading bullet glyphs in authored HTML lines (never shown raw).
const BULLET_RE = /^(?:[✻✳＊*•·※◦▪■]\s*)+/;
const LINE_SPLIT_RE = /<\s*br\s*\/?\s*>|\r?\n/i;
const CJK_RE = /[\u3000-\u9fff\uf900-\ufaff\uff00-\uffef]/;

/** First two sentences of the intro, tags stripped: the hero shows a short lede, never a toggle. */
function shortLede(html) {
  const text = String(html || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  const parts = text.match(/[^。．.!?！？]+[。．.!?！？]?/g) || [text];
  let out = '';
  for (const part of parts) {
    if (out && (out.length + part.length > 220 || out.split(/[。．.!?！？]/).length > 2)) break;
    out += part;
  }
  return out.trim() || text;
}

const COPY = {
  cta: { zh: '預約現場評估', en: 'Book a site assessment', ja: '現場アセスメントを予約' },
  video: { zh: '產品影片', en: 'Product video', ja: '製品動画' },
  play: { zh: '播放影片', en: 'Play video', ja: '動画を再生' },
  spec: { zh: '產品規格', en: 'Specifications', ja: '製品仕様' },
  related: { zh: '相關產品', en: 'Related products', ja: '関連製品' },
  current: { zh: '目前產品', en: 'Current product', ja: '現在の製品' },
  closing: {
    zh: '想知道它在你的場域怎麼運作？',
    en: 'See how it runs on your site.',
    ja: 'あなたの現場でどう動くか、ご覧ください。'
  },
  closingSub: {
    zh: '告訴我們場域條件，我們一起規劃部署方式。',
    en: 'Tell us about the site and we will scope the deployment with you.',
    ja: '現場の条件をお聞かせください。導入方法を一緒に検討します。'
  }
};

/* ---- Tri-language helpers ------------------------------------------------ */

// { zh, en, ja } | string | array -> string (arrays are joined; falls back to en).
function pick(v, lang) {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map((x) => pick(x, lang)).filter(Boolean).join(' ');
  if (typeof v === 'object') {
    const x = v[lang] != null ? v[lang] : (v.en != null ? v.en : (v.zh != null ? v.zh : v.ja));
    return pick(x, lang);
  }
  return '';
}

// Same, but keeps arrays as a list of strings (spec values, legacy lists).
function pickList(v, lang) {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => pick(x, lang)).filter(Boolean);
  if (typeof v === 'object') {
    const x = v[lang] != null ? v[lang] : (v.en != null ? v.en : (v.zh != null ? v.zh : v.ja));
    return pickList(x, lang);
  }
  return [String(v)];
}

function withBase(src) {
  if (!src) return '';
  const s = String(src);
  if (/^(https?:)?\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) return s;
  return BASE + (s.startsWith('/') ? s : '/' + s);
}

const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

// getMedia() keyed by the ORIGINAL yaml path; every returned path gets PUBLIC_URL.
// `registered` marks an explicit registry entry (its kind is trusted, no sniff).
function mediaFor(src) {
  const key = src ? String(src) : '';
  const m = getMedia(key);
  const registered = hasOwn(m, 'stage') || hasOwn(m, 'video') || hasOwn(m, 'alt');
  return {
    ...m,
    registered,
    focal: FOCAL_TWEAKS[key] || m.focal || '50% 50%',
    src: withBase(m.src),
    stage: m.stage ? withBase(m.stage) : undefined,
    stageWebp: m.stageWebp ? withBase(m.stageWebp) : undefined,
    stageM: m.stageM ? withBase(m.stageM) : undefined,
    video: m.video ? {
      mp4: withBase(m.video.mp4),
      webm: m.video.webm ? withBase(m.video.webm) : undefined,
      poster: m.video.poster ? withBase(m.video.poster) : undefined
    } : undefined
  };
}

/**
 * Authored HTML ("＊ line<br />＊ line<br />sentence") -> blocks. Lines that
 * open with a bullet glyph become list items (glyph stripped), the rest
 * stay paragraphs. Inline HTML inside a line is preserved.
 */
function parseBlocks(html) {
  const parts = String(html || '').split(LINE_SPLIT_RE).map((s) => s.trim()).filter(Boolean);
  const blocks = [];
  let list = null;
  parts.forEach((p) => {
    const m = p.match(BULLET_RE);
    if (m) {
      const text = p.slice(m[0].length).trim();
      if (!text) return;
      if (!list) { list = { type: 'ul', items: [] }; blocks.push(list); }
      list.items.push(text);
    } else {
      list = null;
      blocks.push({ type: 'p', text: p });
    }
  });
  return blocks;
}

// A spec cell is set in mono only when every value reads as data (codes,
// numbers, short tokens); a sentence anywhere in the cell makes it text.
function isMonoCell(values) {
  return values.length > 0 && values.every((v) => {
    if (CJK_RE.test(v)) return false;
    const s = v.trim();
    const words = s.split(/\s+/).length;
    const digits = (s.match(/\d/g) || []).length;
    const letters = (s.match(/[A-Za-z]/g) || []).length;
    return (words <= 3 && s.length <= 24) || digits >= letters;
  });
}

function ytId(url) {
  const m = String(url || '').match(YT_RE);
  return m ? m[1] : null;
}

/* ---- Corner sniff --------------------------------------------------------
   Reads the 1 px perimeter of the decoded image (scaled to 40 x 40) and
   reports whether it is transparent (a real cutout) or white (a shot on
   white that has not been re-cut). Results are cached per URL for the page
   lifetime; the layout re-renders once a result lands. Registry entries
   skip this entirely.                                                       */
const SNIFF = {};
const WAIT = {};
const NEUTRAL = { cut: false, white: false };

function sniffImage(src, cb) {
  if (!src || SNIFF[src]) return;
  if (WAIT[src]) { if (WAIT[src].indexOf(cb) < 0) WAIT[src].push(cb); return; }
  WAIT[src] = [cb];
  const settle = (r) => {
    SNIFF[src] = r;
    const q = WAIT[src] || [];
    delete WAIT[src];
    q.forEach((f) => f());
  };
  if (typeof document === 'undefined' || typeof Image === 'undefined') { settle(NEUTRAL); return; }
  const img = new Image();
  img.decoding = 'async';
  img.onload = () => {
    let r = NEUTRAL;
    try {
      // Calibrated on the product set: real photos / UI shots never exceed
      // ~30 % near-white ring pixels, white-plate shots sit at 58-100 %.
      const N = 40;
      const c = document.createElement('canvas');
      c.width = N; c.height = N;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, N, N);
      const d = ctx.getImageData(0, 0, N, N).data;
      let ring = 0, clear = 0, white = 0;
      for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
          if (x > 0 && x < N - 1 && y > 0 && y < N - 1) continue;
          ring++;
          const i = (y * N + x) * 4;
          if (d[i + 3] < 40) clear++;
          else if (Math.min(d[i], d[i + 1], d[i + 2]) > 218) white++;
        }
      }
      r = { cut: clear / ring > 0.6, white: white / ring > 0.45 };
    } catch (e) { r = NEUTRAL; }
    settle(r);
  };
  img.onerror = () => settle(NEUTRAL);
  img.src = src;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ---- Component ----------------------------------------------------------- */

class LayoutB extends Component {
  constructor(props) {
    super(props);
    this.state = { sniffTick: 0, playing: {} };
    this.rootRef = React.createRef();
    this.riseDisposers = [];
    this.seenRise = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
    this.mediaIO = null;
    this.seenMedia = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
    this.resizeTimer = null;
    this.unmounted = false;
    this.bump = this.bump.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  componentDidMount() {
    // React 18 StrictMode unmounts and remounts the same instance in dev:
    // reset everything the unmount tore down.
    this.unmounted = false;
    this.mediaIO = null;
    this.seenMedia = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
    this.riseDisposers = [];
    this.seenRise = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
    this.reduced = prefersReducedMotion();
    this.afterRender();
    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.content !== this.props.content || prevProps.lang !== this.props.lang) {
    }
    this.afterRender();
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    this.riseDisposers.forEach((d) => d());
    this.riseDisposers = [];
    if (this.mediaIO) { this.mediaIO.disconnect(); this.mediaIO = null; }
  }

  // Arm reveals for new nodes (content arrives async), lazily sniff media,
  // measure the lede. Runs after every render, so everything is idempotent.
  afterRender() {
    const root = this.rootRef.current;
    if (!root) return;
    this.armRise(root);
    this.watchMedia(root);
    this.measure();
  }

  // Each .rise node is observed exactly once and its observer lives until
  // unmount: re-arming on every update would disconnect observers before
  // their pending callbacks deliver and leave sections invisible.
  armRise(root) {
    const nodes = Array.from(root.querySelectorAll('.rise'));
    nodes.forEach((n) => {
      if (this.seenRise) { if (this.seenRise.has(n)) return; this.seenRise.add(n); }
      this.riseDisposers.push(observeRise(n));
      if (this.seenRise) n.querySelectorAll('.rise').forEach((c) => this.seenRise.add(c));
    });
  }

  watchMedia(root) {
    const nodes = Array.from(root.querySelectorAll('.tpb-media[data-sniff]'));
    if (nodes.length === 0) return;
    const lazyOK = 'IntersectionObserver' in window;
    if (lazyOK && !this.mediaIO) {
      this.mediaIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          this.mediaIO.unobserve(e.target);
          sniffImage(e.target.getAttribute('data-sniff'), this.bump);
        });
      }, { rootMargin: '600px 0px' });
    }
    nodes.forEach((n) => {
      if (this.seenMedia) { if (this.seenMedia.has(n)) return; this.seenMedia.add(n); }
      const src = n.getAttribute('data-sniff');
      if (SNIFF[src]) return;
      if (!lazyOK || n.hasAttribute('data-eager')) sniffImage(src, this.bump);
      else this.mediaIO.observe(n);
    });
  }

  bump() {
    if (this.unmounted) return;
    this.setState((s) => ({ sniffTick: s.sniffTick + 1 }));
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => { if (!this.unmounted) this.measure(); }, 120);
  }

  measure() {}


  play(id) {
    this.setState((s) => ({ playing: { ...s.playing, [id]: true } }));
  }

  lang() {
    return LANGS.includes(this.props.lang) ? this.props.lang : 'en';
  }

  // Primary click goes through props.navigate; modified clicks keep the href.
  go(route, e) {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
    if (typeof this.props.navigate !== 'function') return;
    e.preventDefault();
    this.props.navigate(route);
  }

  /* ---- Media ------------------------------------------------------------- */

  resolveKind(m) {
    if (m.kind === 'gif' || m.video) return 'gif';
    if (m.registered) return m.kind || 'photo';
    const s = SNIFF[m.src];
    if (s) {
      if (s.cut) return 'cutout';
      if (s.white) return 'white';
      if (m.kind === 'white') return 'photo';
    }
    return m.kind || 'photo';
  }

  isReady(m) {
    if (m.kind === 'gif' || m.video || m.registered) return true;
    if (typeof window === 'undefined') return true;
    return !!SNIFF[m.src];
  }

  /**
   * One media slot. The wrapper is absolutely positioned inside its tile
   * (position: relative on the tile gives it its size). `alt` is plain text;
   * opts: { eager, stage (use the staged render when the registry has one),
   *         mobileStage (portrait stageM under 781 px) }
   */
  renderMedia(m, alt, opts = {}) {
    if (!m || !m.src) return null;
    const pos = { objectPosition: m.focal || '50% 50%' };
    const lazy = opts.eager ? undefined : 'lazy';
    if (opts.stage && m.stage) {
      return (
        <div className="tpb-media tpb-media--photo tpb-media--staged is-ready">
          <picture>
            {m.stageM && opts.mobileStage && <source media="(max-width: 780px)" type="image/webp" srcSet={m.stageM} />}
            {m.stageWebp && <source type="image/webp" srcSet={m.stageWebp} />}
            <img className="tpb-media__img" style={pos} src={m.stage} alt={alt} decoding="async" loading={lazy} />
          </picture>
        </div>
      );
    }
    const kind = this.resolveKind(m);
    const ready = this.isReady(m);
    const cls = 'tpb-media tpb-media--' + kind + (ready ? ' is-ready' : '');
    const sniffAttrs = (kind === 'gif' || m.registered) ? {} : { 'data-sniff': m.src, 'data-eager': opts.eager ? '' : undefined };

    if (kind === 'gif') {
      if (m.video) {
        const autoplay = !this.reduced;
        return (
          <div className={cls}>
            <video className="tpb-media__img" style={pos} aria-hidden="true"
              autoPlay={autoplay} muted loop playsInline preload={autoplay ? 'auto' : 'metadata'}
              poster={m.video.poster}
              ref={(el) => { if (el) { el.muted = true; el.defaultMuted = true; } }}>
              {m.video.webm && <source src={m.video.webm} type="video/webm" />}
              <source src={m.video.mp4} type="video/mp4" />
            </video>
          </div>
        );
      }
      return (
        <div className={cls}>
          <img className="tpb-media__img" style={pos} src={m.src} alt={alt} loading={lazy} decoding="async" />
        </div>
      );
    }

    if (kind === 'cutout') {
      // CSS stage: spotlight, floor line + soft shadow, contained render.
      return (
        <div className={cls} {...sniffAttrs}>
          <span className="tpb-stage__spot" aria-hidden="true"></span>
          <span className="tpb-stage__floor" aria-hidden="true"></span>
          <img className="tpb-stage__img" src={m.src} alt={alt} loading={lazy} decoding="async" />
        </div>
      );
    }

    if (kind === 'white') {
      // Multiply onto the light pool: white background -> the spotlight itself.
      return (
        <div className={cls} {...sniffAttrs}>
          <span className="tpb-stage__floor" aria-hidden="true"></span>
          <img className="tpb-stage__wash" style={pos} src={m.src} alt={alt} loading={lazy} decoding="async" />
        </div>
      );
    }

    // photo / ui: edge to edge, cover, focal.
    return (
      <div className={cls} {...sniffAttrs}>
        <img className="tpb-media__img" style={pos} src={m.src} alt={alt} loading={lazy} decoding="async" />
      </div>
    );
  }

  // Authored HTML with bullet lines -> <ul> with square markers + <p>.
  renderRich(html, className) {
    const blocks = parseBlocks(html);
    if (blocks.length === 0) return null;
    return (
      <div className={className}>
        {blocks.map((b, i) => (
          b.type === 'ul'
            ? <ul key={i} className="tpb-list">{b.items.map((t, j) => <li key={j} dangerouslySetInnerHTML={{ __html: t }} />)}</ul>
            : <p key={i} dangerouslySetInnerHTML={{ __html: b.text }} />
        ))}
      </div>
    );
  }

  // Section landmark: one H2 in the bleed padding above its band.
  renderSectionHead(text) {
    return (
      <div className="tpb-sec-head bleed rise">
        <h2>{text}</h2>
      </div>
    );
  }

  /* ---- Sections ---------------------------------------------------------- */

  renderHero(intro, lang, t) {
    const title = pick(this.props.title, lang);
    const model = pick(this.props.model, lang);
    const m = intro && intro.img ? mediaFor(intro.img) : null;
    const kind = m ? this.resolveKind(m) : 'none';
    const variant = !m ? 'none'
      : (m.stage ? 'stage'
        : kind === 'cutout' ? 'stage'
          : kind === 'white' ? 'wash'
            : kind === 'ui' ? 'screen'
              : 'photo');
    const alt = (m && pick(m.alt, lang)) || title;
    // An authored "<< status >>" prefix becomes a small tag above the lede
    // instead of spending the lede's visible lines on it.
    let lede = intro ? pick(intro.content, lang) : '';
    let tag = '';
    const tm = lede.match(STATUS_RE);
    if (tm) { tag = tm[1].trim(); lede = lede.slice(tm[0].length); }
    return (
      <section className={'tpb-hero tpb-hero--' + variant} aria-label={title}>
        {m && (
          <div className="tpb-hero__media">
            {this.renderMedia(m, alt, { eager: true, stage: true, mobileStage: true })}
            {m.stage && <span className="tpb-hero__grid" aria-hidden="true"></span>}
            {m.stage && <span className="tpb-hero__fx" aria-hidden="true"></span>}
            <span className="tpb-hero__scrim" aria-hidden="true"></span>
          </div>
        )}
        <div className="tpb-hero__copy bleed rise">
          {model && <p className="tpb-hero__model tpb-mono">{model}</p>}
          <h1 className="h-display tpb-hero__title">{title}</h1>
          {tag && <p className="tpb-hero__tag">{tag}</p>}
          {lede && (
            <div className="p-lede tpb-hero__lede" dangerouslySetInnerHTML={{ __html: shortLede(lede) }} />
          )}
        </div>
      </section>
    );
  }

  renderGlance(item, lang, key) {
    return (
      <section key={key} className="tpb-glance">
        <div className="tpb-glance__half tpb-glance__half--cus rise">
          <h2 className="tpb-glance__title">{pick(item.title_cus, lang)}</h2>
          {this.renderRich(pick(item.content_cus, lang), 'tpb-glance__body')}
        </div>
        <div className="tpb-glance__half tpb-glance__half--ps rise">
          <h2 className="tpb-glance__title">{pick(item.title_ps, lang)}</h2>
          {this.renderRich(pick(item.content_ps, lang), 'tpb-glance__body')}
        </div>
      </section>
    );
  }

  renderMosaic(items, lang, key) {
    return (
      <section key={key} className="tpb-mosaic">
        {items.map((it, i) => {
          const title = pick(it.title, lang);
          const m = it.img ? mediaFor(it.img) : null;
          const alt = (m && pick(m.alt, lang)) || title;
          return (
            <div key={i} className={'tpb-pair' + (i % 2 === 1 ? ' tpb-pair--flip' : '')}>
              <div className="tpb-tile tpb-tile--media rise">
                {m && this.renderMedia(m, alt, { stage: true })}
              </div>
              <div className="tpb-tile tpb-tile--text rise">
                {title && <h3 className="tpb-tile__title">{title}</h3>}
                <div className="tpb-tile__body" dangerouslySetInnerHTML={{ __html: pick(it.content, lang) }} />
              </div>
            </div>
          );
        })}
      </section>
    );
  }

  // YouTube tiles start as a poster facade (no black wall, no iframe cost)
  // and swap to the autoplaying iframe on click; gif links become loops.
  renderVideoTile(link, id, t, i) {
    const s = String(link);
    const label = t(COPY.video) + ' ' + (i + 1);
    if (!/^https?:\/\//i.test(s)) return this.renderMedia(mediaFor(s), label);
    const yt = ytId(s);
    const playing = !!this.state.playing[id];
    if (!yt || playing) {
      const src = playing ? s + (s.includes('?') ? '&' : '?') + 'autoplay=1' : s;
      return (
        <iframe
          src={src}
          title={label}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    const hq = 'https://i.ytimg.com/vi/' + yt + '/hqdefault.jpg';
    const max = 'https://i.ytimg.com/vi/' + yt + '/maxresdefault.jpg';
    // maxresdefault is missing on some videos: YouTube then serves a 120 px
    // placeholder with a 200, so fall back on size as well as on error.
    const fallback = (e) => {
      const el = e.currentTarget;
      if (el.dataset.fb === '1') return;
      if (e.type === 'error' || el.naturalWidth <= 120) { el.dataset.fb = '1'; el.src = hq; }
    };
    return (
      <button type="button" className="tpb-video__facade" onClick={() => this.play(id)} aria-label={t(COPY.play) + ' ' + (i + 1)}>
        <img className="tpb-video__poster" src={max} alt="" loading="lazy" decoding="async" onLoad={fallback} onError={fallback} />
        <span className="tpb-video__play" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
        </span>
      </button>
    );
  }

  renderVideo(item, lang, t, key) {
    const links = Array.isArray(item.video_link) ? item.video_link.filter(Boolean) : [];
    if (links.length === 0) return null;
    const cols = links.length === 1 ? 1 : (links.length === 3 ? 3 : 2);
    return (
      <section key={key} className="tpb-video">
        {this.renderSectionHead(t(COPY.video))}
        <div className={'tpb-video__grid tpb-video__grid--n' + cols}>
          {links.map((link, i) => (
            <div key={i} className="tpb-video__tile rise">
              {this.renderVideoTile(link, key + '-' + i, t, i)}
            </div>
          ))}
        </div>
      </section>
    );
  }

  renderSpec(item, lang, t, key) {
    const titles = Array.isArray(item.title) ? item.title : [];
    const values = Array.isArray(item.content) ? item.content : [];
    const rows = titles.map((tt, i) => ({ label: pick(tt, lang), values: pickList(values[i], lang) }))
      .filter((r) => r.label || r.values.length);
    const m = item.img ? mediaFor(item.img) : null;
    const alt = (m && pick(m.alt, lang)) || (pick(this.props.title, lang) + ' ' + pick(this.props.model, lang)).trim();
    return (
      <section key={key} className="tpb-spec-sec">
        {this.renderSectionHead(t(COPY.spec))}
        <div className="tpb-spec">
          <div className="tpb-spec__stage rise">
            {m && this.renderMedia(m, alt)}
          </div>
          <div className="tpb-spec__table rise">
            <table>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <th scope="row">{r.label}</th>
                    <td className={isMonoCell(r.values) ? 'tpb-mono' : 'tpb-spec__text'}>
                      {r.values.map((v, j) => <span key={j}>{v}</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  renderRelated(item, lang, t, key) {
    const items = Array.isArray(item.items) ? item.items : [];
    if (items.length === 0) return null;
    const heading = pick(item.section_title, lang) || t(COPY.related);
    return (
      <section key={key} className="tpb-related">
        {this.renderSectionHead(heading)}
        <div className="tpb-related__grid">
          {items.map((rp, i) => {
            const current = rp.id === this.props.id;
            const route = '/tech/' + rp.id;
            const title = pick(rp.title, lang);
            const m = rp.img ? mediaFor(rp.img) : null;
            const alt = (m && pick(m.alt, lang)) || title;
            return (
              <a
                key={i}
                className={'tpb-rel rise' + (current ? ' is-current' : '')}
                href={buildPath(lang, route)}
                aria-current={current ? 'page' : undefined}
                tabIndex={current ? -1 : 0}
                onClick={(e) => {
                  if (current) { e.preventDefault(); return; }
                  trackEvent('select_product', { location: 'product_related', product_name: pick(rp.title, 'en'), product_route: route });
                  this.go(route, e);
                }}
              >
                <span className="tpb-rel__media">
                  {m && this.renderMedia(m, alt)}
                </span>
                <span className="tpb-rel__caption">
                  {current && <span className="tpb-rel__current tpb-mono">{t(COPY.current)}</span>}
                  <span className="tpb-rel__title">{title}</span>
                  <span className="tpb-rel__desc">{pick(rp.desc, lang)}</span>
                </span>
              </a>
            );
          })}
        </div>
      </section>
    );
  }

  // Legacy YAML types (word_section, list_section, slide_show, icon_word,
  // pic_display, title_section, ...): title + text, nothing else, so no page
  // can crash and no white box can appear.
  renderPlain(item, lang, key) {
    const title = pick(item.main_title, lang) || pick(item.title, lang);
    let lines = [];
    if (item.type === 'video_display' && item.video_src) {
      return (
        <section key={key} className="tpb-video">
          <div className="tpb-video__grid tpb-video__grid--n1">
            <div className="tpb-video__tile rise">
              <iframe src={item.video_src} title="video" loading="lazy" allowFullScreen />
            </div>
          </div>
        </section>
      );
    }
    if (item.content != null) lines = pickList(item.content, lang);
    if (item.type === 'tech_intro') lines = [pick(item.content, lang)];
    if (!title && lines.length === 0) return null;
    return (
      <section key={key} className="tpb-plain bleed rise">
        {title && <h2 className="tpb-plain__title">{title}</h2>}
        {lines.length > 0 && (
          <div className="tpb-plain__body">
            {lines.map((l, i) => <p key={i} dangerouslySetInnerHTML={{ __html: l }} />)}
          </div>
        )}
      </section>
    );
  }

  renderClosing(lang, t) {
    const contactHref = buildPath(lang, CONTACT_ROUTE);
    return (
      <section className="tpb-close bleed">
        <div className="tpb-close__copy rise">
          <h2>{t(COPY.closing)}</h2>
          <p>{t(COPY.closingSub)}</p>
        </div>
        <div className="tpb-close__action rise">
          <a className="btn-primary" href={contactHref} onClick={(e) => this.go(CONTACT_ROUTE, e)}>
            {t(COPY.cta)}
            <span className="arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </section>
    );
  }

  /* ---- Render ------------------------------------------------------------ */

  render() {
    const lang = this.lang();
    const t = (o) => o[lang] || o.en;
    const content = this.props.content && typeof this.props.content === 'object' ? this.props.content : {};

    // Flatten groups in YAML order; the first tech_intro feeds the hero,
    // consecutive feature items merge into one mosaic.
    const items = [];
    Object.keys(content).forEach((g) => {
      (Array.isArray(content[g]) ? content[g] : []).forEach((it) => { if (it && it.type) items.push(it); });
    });
    const introIdx = items.findIndex((it) => it.type === 'tech_intro');
    const intro = introIdx >= 0 ? items[introIdx] : null;
    const blocks = [];
    let run = null;
    items.forEach((it, i) => {
      if (i === introIdx) return;
      if (FEATURE_TYPES.includes(it.type)) {
        if (!run) { run = { type: '__mosaic', items: [] }; blocks.push(run); }
        run.items.push(it);
      } else {
        run = null;
        blocks.push(it);
      }
    });

    return (
      <div className="tpb" ref={this.rootRef}>
        {this.renderHero(intro, lang, t)}
        {blocks.map((b, i) => {
          switch (b.type) {
            case '__mosaic': return this.renderMosaic(b.items, lang, i);
            case 'tech_cus_and_ps': return this.renderGlance(b, lang, i);
            case 'video_gallery': return this.renderVideo(b, lang, t, i);
            case 'spec_item': return this.renderSpec(b, lang, t, i);
            case 'related_products': return this.renderRelated(b, lang, t, i);
            default: return this.renderPlain(b, lang, i);
          }
        })}
        {this.renderClosing(lang, t)}
      </div>
    );
  }
}

export default function ProductLayout(props) {
  return <LayoutB {...props} />;
}
